(function() {
    // ─────────────────────────────────────────────────────────────────────
    // What was wrong with the original version of this file, and why:
    //
    // 1. BROKEN IMAGE / TITLE (the bug you reported): updateProductInfo()
    //    looked for the product title and image using selectors from
    //    Shopify's older Dawn-family themes:
    //        .product__title, [itemprop="name"]
    //        .product__media img, .product__image img
    //    This store runs Shopify's newer Horizon theme, which doesn't use
    //    either of those. Verified directly against the live page: neither
    //    selector matches anything, so data-sticky-atc-title and
    //    data-sticky-atc-image were permanently left empty. The price
    //    happened to still show only because `.price` genuinely exists in
    //    Horizon too — that was a coincidence, not evidence the rest worked.
    //    Fixed below by adding the real selectors that were confirmed
    //    against the live DOM (`h1`, `media-gallery img`), kept as
    //    fallbacks after the original selectors so this still works if it's
    //    ever reused on an older-theme store.
    //
    // 2. Competing with the theme's OWN built-in sticky-add-to-cart bar:
    //    that's a separate, structural issue (two independent widgets on
    //    one page) — not something a single script file can fix by itself.
    //    This rewrite only fixes what's contained to this file; the
    //    duplicate-widget question is a theme-editor decision, not a code one.
    //
    // 3. Visibility could get stuck: the show/hide decision and the
    //    IntersectionObserver callback were the same code path with no
    //    debounce, so a fast scroll or a layout shift right at the
    //    threshold could show/hide it more than once in a row. Consolidated
    //    into one #updateVisibility() below, with a short hide-only debounce
    //    — the same pattern used to fix the theme's native sticky bar.
    //
    // 4. The button assumed clicking it always worked: it never checked
    //    whether the add-to-cart request actually succeeded before it would
    //    have looked "done" — if the real button's request failed (out of
    //    stock, network error), there was no feedback either way. Now it
    //    disables itself and shows "Adding…" while the request is in
    //    flight, and confirms success by watching the header cart icon
    //    (the same element the theme's own sticky bar flies an image
    //    toward) rather than guessing.
    // ─────────────────────────────────────────────────────────────────────

    const HIDE_DEBOUNCE_MS = 200;
    const ADD_CONFIRM_TIMEOUT_MS = 4000;

    class StickyAddToCartayw1vcui2ym9wrzdwcaigenblock1c71afdc68pcb extends HTMLElement {
      constructor() {
        super();
        this.mainAddToCartButton = null;
        this.stickyButton = null;
        this.productForm = null;
        this.scrollThreshold = 50;

        // Single source of truth for visibility — see note (3) above.
        this.isVisible = false;
        this.scrollObserver = null;
        this.hideDebounceTimeout = null;

        // Keeps image/title/price in sync — see note (1) above.
        this.priceObserver = null;
        this.variantChangeHandler = null;

        this.defaultButtonLabel = 'Add to Cart';
      }

      connectedCallback() {
        this.stickyButton = this.querySelector('[data-sticky-atc-button]');
        this.productForm = document.querySelector('form[action*="/cart/add"]');

        // Every Shopify theme generation needs a name="add" submit control
        // for the native /cart/add form post, so it's the most reliable
        // selector to lead with. .product-form__submit is kept as a
        // fallback for older Dawn-family themes.
        this.mainAddToCartButton = document.querySelector('[name="add"], .product-form__submit');

        if (!this.mainAddToCartButton || !this.productForm || !this.stickyButton) {
          console.warn('Sticky ATC: main add-to-cart button, product form, or sticky button not found');
          return;
        }

        this.defaultButtonLabel = this.stickyButton.textContent.trim() || this.defaultButtonLabel;

        this.setupScrollObserver();
        this.setupButtonClick();
        this.updateProductInfo();
        this.observeProductChanges();
      }

      disconnectedCallback() {
        this.scrollObserver?.disconnect();
        this.priceObserver?.disconnect();
        if (this.hideDebounceTimeout) clearTimeout(this.hideDebounceTimeout);
        if (this.variantChangeHandler) {
          document.removeEventListener('change', this.variantChangeHandler);
        }
      }

      // --- Visibility -------------------------------------------------

      setupScrollObserver() {
        this.scrollObserver = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            if (!entry) return;
            const shouldShow = !entry.isIntersecting && window.scrollY > this.scrollThreshold;
            this.#updateVisibility(shouldShow);
          },
          { rootMargin: '0px', threshold: 0 }
        );
        this.scrollObserver.observe(this.mainAddToCartButton);
      }

      /**
       * The only place that changes `is-visible`. Showing is immediate;
       * hiding waits briefly and re-confirms, so a quick bounce right at
       * the show/hide threshold can't toggle the bar twice in a row.
       * @param {boolean} shouldShow
       */
      #updateVisibility(shouldShow) {
        if (shouldShow === this.isVisible) return;

        if (shouldShow) {
          if (this.hideDebounceTimeout) {
            clearTimeout(this.hideDebounceTimeout);
            this.hideDebounceTimeout = null;
          }
          this.isVisible = true;
          this.classList.add('is-visible');
          return;
        }

        if (this.hideDebounceTimeout) return;
        this.hideDebounceTimeout = setTimeout(() => {
          this.hideDebounceTimeout = null;
          this.isVisible = false;
          this.classList.remove('is-visible');
        }, HIDE_DEBOUNCE_MS);
      }

      // --- Add to cart --------------------------------------------------

      setupButtonClick() {
        this.stickyButton.addEventListener('click', () => this.#handleAddToCart());
      }

      async #handleAddToCart() {
        if (this.mainAddToCartButton.disabled || this.stickyButton.disabled) return;

        this.stickyButton.disabled = true;
        this.stickyButton.textContent = 'Adding…';

        this.mainAddToCartButton.click();

        const success = await this.#waitForCartToUpdate();

        this.stickyButton.textContent = success ? 'Added ✓' : this.defaultButtonLabel;
        if (success) {
          setTimeout(() => {
            this.stickyButton.textContent = this.defaultButtonLabel;
          }, 1500);
        }
        this.stickyButton.disabled = this.mainAddToCartButton.disabled;
      }

      /**
       * Confirms the click actually added something rather than assuming
       * it did. Watches the header cart icon (the same element the theme's
       * own sticky bar targets for its fly-to-cart animation) for any
       * change, since that's theme-agnostic and doesn't depend on knowing
       * this store's exact cart-event names. Falls back to "assume success"
       * after a few seconds so the button can never get stuck disabled.
       * @returns {Promise<boolean>}
       */
      #waitForCartToUpdate() {
        const cartIcon = document.querySelector('.header-actions__cart-icon, [data-cart-count], .cart-count-bubble');
        if (!cartIcon) return Promise.resolve(true); // nothing to watch — don't block on it

        return new Promise((resolve) => {
          let settled = false;
          const finish = (result) => {
            if (settled) return;
            settled = true;
            observer.disconnect();
            clearTimeout(timeout);
            resolve(result);
          };

          const observer = new MutationObserver(() => finish(true));
          observer.observe(cartIcon, { subtree: true, childList: true, attributes: true, characterData: true });

          const timeout = setTimeout(() => finish(true), ADD_CONFIRM_TIMEOUT_MS);
        });
      }

      // --- Keeping title / price / image in sync -------------------------

      updateProductInfo() {
        const productTitle = document.querySelector('.product__title, [itemprop="name"], h1');
        const productPrice = document.querySelector('.price, [data-product-price], .product__price');
        const productImage = document.querySelector(
          '.product__media img, .product__image img, media-gallery img, .product-gallery img'
        );

        if (productTitle) {
          const titleElement = this.querySelector('[data-sticky-atc-title]');
          if (titleElement) titleElement.textContent = productTitle.textContent.trim();
        }

        if (productPrice) {
          const priceElement = this.querySelector('[data-sticky-atc-price]');
          if (priceElement) priceElement.innerHTML = productPrice.innerHTML;
        }

        if (productImage) {
          const imageElement = this.querySelector('[data-sticky-atc-image]');
          if (imageElement) {
            imageElement.src = productImage.currentSrc || productImage.src;
            imageElement.alt = productImage.alt || '';
          }
        }

        this.updateVariantInfo();
        this.stickyButton.disabled = this.mainAddToCartButton.disabled;
      }

      updateVariantInfo() {
        const variantElement = this.querySelector('[data-sticky-atc-variant]');
        if (!variantElement) return;

        const selectedVariants = [];
        const variantInputs = document.querySelectorAll(
          'variant-selects select, variant-radios input:checked, variant-picker select, variant-picker input:checked'
        );

        variantInputs.forEach((input) => {
          if (input.tagName === 'SELECT') {
            const selectedOption = input.options[input.selectedIndex];
            if (selectedOption && selectedOption.value) {
              selectedVariants.push(selectedOption.textContent.trim());
            }
          } else if (input.tagName === 'INPUT') {
            selectedVariants.push(input.value);
          }
        });

        variantElement.textContent = selectedVariants.join(' / ');
      }

      /**
       * Re-syncs title/price/image/button-state whenever anything about the
       * product on the page changes. Watching the price container (rather
       * than requiring a specific variant-picker element to exist) means
       * this keeps working even on a single-variant product like this one,
       * or if the theme changes how its variant picker is built.
       */
      observeProductChanges() {
        const priceContainer = document.querySelector('.price, [data-product-price], .product__price');

        if (priceContainer) {
          this.priceObserver = new MutationObserver(() => this.updateProductInfo());
          this.priceObserver.observe(priceContainer, { subtree: true, childList: true, characterData: true });
        }

        this.variantChangeHandler = (event) => {
          if (event.target.matches?.('variant-selects select, variant-radios input, variant-picker select, variant-picker input')) {
            this.updateProductInfo();
          }
        };
        document.addEventListener('change', this.variantChangeHandler);
      }
    }

    if (!customElements.get('sticky-add-to-cart-ayw1vcui2ym9wrzdwcaigenblock1c71afdc68pcb')) {
      customElements.define(
        'sticky-add-to-cart-ayw1vcui2ym9wrzdwcaigenblock1c71afdc68pcb',
        StickyAddToCartayw1vcui2ym9wrzdwcaigenblock1c71afdc68pcb
      );
    }
  })();